export const BackoffMode = ({
  NONE: 'none',
  LINEAR: 'linear',
  EXPONENTIAL: 'exponential'
} as const);
export type BackoffMode = (typeof BackoffMode)[keyof typeof BackoffMode];
export class RetryBudget {
  private consecutiveFailures = 0;
  private inFlight: Promise<any> | null = null;
  constructor(private config: {
    maxRetries: number;
    baseBackoffMs?: number;
    backoffMode?: BackoffMode;
  }) {}
  canAttempt(): boolean {
    return this.consecutiveFailures < this.config.maxRetries;
  }
  get attempts() {
    return this.consecutiveFailures;
  }
  get exhausted() {
    return this.consecutiveFailures >= this.config.maxRetries;
  }

  /**
   * Compute how long to wait *before* the next retry,
   * based on current consecutive failure count.
   * Does not mutate internal state.
   */
  getNextBackoffMs(): number {
    const base = this.config.baseBackoffMs ?? 0;
    if (!base || this.consecutiveFailures === 0) {
      return 0;
    }
    const n = this.consecutiveFailures;
    const mode = this.config.backoffMode ?? 'none';
    if (mode === BackoffMode.EXPONENTIAL) {
      return base * Math.pow(2, n - 1);
    }
    if (mode === BackoffMode.LINEAR) {
      return base * n;
    }
    return 0;
  }

  /** Record a failed attempt and return backoff for the *next* try. */
  registerFailure(): number {
    this.consecutiveFailures = Math.min(this.consecutiveFailures + 1, this.config.maxRetries);
    return this.getNextBackoffMs();
  }
  reset(): void {
    this.consecutiveFailures = 0;
  }

  /** Single-flight logic */
  ensureInFlight<T>(factory: () => Promise<T>, beforeStart?: () => Promise<void> | void): Promise<T> {
    if (this.inFlight) {
      return (this.inFlight as Promise<T>);
    }
    const run = async () => {
      if (beforeStart) {
        await beforeStart();
      }
      try {
        return await factory();
      } finally {
        this.inFlight = null;
      }
    };
    const p = run();
    this.inFlight = p;
    return p;
  }
}